import { useCallback, useMemo, useState } from "react";

import { ASSESSMENT_VIEW_QUERY_PARAM } from "components/constants";
import {
  MappedAllAssessmentTableRow,
  getAssessmentStatusLabel,
} from "components/materials/section/assessment/utils";
import { SortDirection } from "mds/components/SortableTable";
import { useCapabilities } from "store/capability-hooks";
import { selectLateSubmissionCounts } from "store/selectors/computed";
import { useObjectSelector } from "store/store-hooks";
import { useQueryParam } from "utils/routing";

export const useIsAssessing = () => {
  const { canAuthorSection } = useCapabilities();
  const isAssessing = useQueryParam(ASSESSMENT_VIEW_QUERY_PARAM);

  if (!canAuthorSection) {
    return false;
  }
  // Instructors can assess, so check the query param
  // to see if they are currently assessing
  return !!isAssessing;
};

export type AllAssessmentDashboardDataType = {
  allAssessmentsCSVData: {
    headers: string[];
    rows: (string | number)[][];
  };
  allAssessmentsTableRows: MappedAllAssessmentTableRow[];
};

export type AssessmentStatusTableSortField =
  | "topic"
  | "page"
  | "status"
  | "due_date"
  | "publishing"
  | "submissions"
  | "progress"
  | "assessment_category"
  | "avg_points"
  | "outcomes"
  | "last_submitted"
  | "last_assessed";

const getSortValue = (
  row: MappedAllAssessmentTableRow,
  field: AssessmentStatusTableSortField,
  isArchived: boolean,
): number | string => {
  switch (field) {
    case "topic":
      return row.topicTitle ?? "";
    case "page":
      return row.title;
    case "status":
      return getAssessmentStatusLabel({
        isMarkedComplete: row.isMarkedComplete,
        assessedSubmissionsCount: row.assessedSubmissionsCount,
        isAssessmentComplete: row.isAssessmentComplete,
        isArchived,
      });
    case "due_date":
      return row.dueAtRaw ?? "";
    case "publishing":
      return row.publishStatus;
    case "submissions":
      return row.totalSubmissionsCount;
    case "progress": {
      if (row.totalSubmissionsCount === 0) return -1;
      return row.assessedSubmissionsCount / row.totalSubmissionsCount;
    }
    case "assessment_category":
      return row.assessmentCategory;
    case "avg_points": {
      const parsed = parseFloat(row.avgPoints);
      return isNaN(parsed) ? -1 : parsed;
    }
    case "outcomes":
      return row.outcomesCount;
    case "last_submitted":
      return row.lastSubmittedRaw ?? "";
    case "last_assessed":
      return row.lastAssessedRaw ?? "";
    default:
      return 0;
  }
};

export const useSortableRows = (
  rows: MappedAllAssessmentTableRow[],
  defaultSortField?: AssessmentStatusTableSortField,
  defaultSortDirection: SortDirection = "desc",
  isArchived = false,
) => {
  const [sortField, setSortField] = useState<AssessmentStatusTableSortField | null>(
    defaultSortField ?? null,
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    defaultSortField ? defaultSortDirection : "asc",
  );

  const handleSort = useCallback(
    (field: AssessmentStatusTableSortField) => {
      if (sortField === field) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
    },
    [sortField],
  );

  const sortedRows = useMemo(() => {
    if (!sortField) return rows;
    return [...rows].sort((a, b) => {
      const aVal = getSortValue(a, sortField, isArchived);
      const bVal = getSortValue(b, sortField, isArchived);
      let cmp = 0;
      if (typeof aVal === "string" && typeof bVal === "string") {
        cmp = aVal.localeCompare(bVal);
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        cmp = aVal - bVal;
      }
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [rows, sortField, sortDirection, isArchived]);

  return { sortedRows, sortField, sortDirection, handleSort };
};

export const useLateSubmissionCounts = () =>
  useObjectSelector(selectLateSubmissionCounts, ["totalLateCount", "newLateCount"]);
